import {useEffect, useRef} from "react";
import useMountedRef from "./useMountedRef";
import * as Sentry from "@sentry/react";

const usePolling = ({active, callback}) => {
    const mountedRef = useMountedRef();

    // This variable keeps the timer id
    const timer = useRef(-1);

    // This variable keep track of the last callback. No matter if the user change the callback function the last one
    // will always be used while the polling is still going (no restart in case the function changes)
    const savedCallback = useRef(callback);
    useEffect(
        () => {savedCallback.current = callback},
        [callback]
    )

    const tick = (attempts = 1, timeout) => {
        const maxTimeout = 60000;
        const initialTimeout = 1875;
        const attemptsBeforeDoublingWait = 8;

        const currentTimeout = timeout || initialTimeout;

        const innerTick = async () => {
            timer.current = -1;
            try{
                await savedCallback.current();
            }catch(e){
                Sentry.captureException(e);
                return;  // Stop polling if an exception occurred
            }

            if (active) {
                let nextTimeout = maxTimeout;
                if (currentTimeout < maxTimeout) {
                    nextTimeout = (2 ** Math.floor(attempts / attemptsBeforeDoublingWait)) * initialTimeout;
                }
                tick(attempts + 1, nextTimeout);
            }
        }

        if (timer.current < 0 && mountedRef.current) {
            timer.current = setTimeout(innerTick, currentTimeout);
            console.log(`Scheduled next tick in ${currentTimeout}ms`);
        } else {
            console.warn("An instance of the same polling hooks is already running or component is unmounted");
        }
    }

    useEffect(() => {
        if (timer.current < 0 && active) {
            console.log("Starting polling process...")
            tick();
        }
        if(!active){ // clear when active is set to false
            clearTimeout(timer.current);
            timer.current = -1;
        }
        // Clear alloggiati Timer
        return () => clearTimeout(timer.current);
    }, [active]);  // eslint-disable-line react-hooks/exhaustive-deps
}

export default usePolling;